/* ═══════════════════════════════════════════════════════════════════════════
   LES TROIS DIFFICULTÉS.

   ⚠️ La règle qui gouverne ce fichier vient du plan (§6.7) et elle est
   contre-intuitive : « les difficultés changent la NATURE des patterns, pas
   seulement les nombres ».

   La solution facile — multiplier la vitesse des projectiles et la cadence par
   0,8 / 1,0 / 1,3 — produit trois versions du même jeu dont deux sont moins
   bonnes. En Recrue, tout devient mou et le joueur n'apprend rien qui lui serve
   ensuite ; en Vétéran, les mêmes figures deviennent simplement plus rapides
   que le temps de réaction humain, ce qui n'est pas de la difficulté mais du
   hasard.

   Chaque palier modifie donc ce que le joueur doit COMPRENDRE :

     Recrue     Le scellement de tir est large (140 u) : s'approcher d'un ennemi
                le fait taire. C'est une porte de sortie permanente, et elle
                enseigne une vraie leçon du genre — l'agressivité paie.
                Le frôlement n'y rapporte rien : pousser un débutant à raser
                les projectiles pour marquer, c'est lui apprendre à mourir.

     Opérateur  La référence. Toutes les valeurs du bestiaire, des boss et de la
                courbe de puissance sont réglées ici, et c'est le seul palier où
                un score veut dire quelque chose sans qualificatif.

     Vétéran    Le scellement se réduit à 70 u : on ne se met plus à l'abri en
                collant l'ennemi. Et les éventails STATIQUES se mettent à
                tourner d'une volée à l'autre, ce qui supprime la place sûre
                mémorisable. C'est le vrai changement de nature — en Opérateur
                on apprend une figure et on s'y installe, en Vétéran il faut la
                relire à chaque volée.

   ── Correction d'une erreur de lecture du plan ──
   La première version de ce fichier affirmait que la difficulté ne devait
   toucher NI les points de vie NI les récompenses, « pour que deux scores
   restent comparables ». C'était faux, et la faute mérite d'être conservée
   ici parce qu'elle est facile à refaire.

   Cette règle existe bel et bien dans le plan — mais elle concerne le RANK
   CACHÉ (§6.10, « le rank ne touche jamais les PV ni les récompenses »), qui
   est un curseur invisible s'ajustant en cours de partie. Le palier de
   difficulté, lui, est choisi explicitement par le joueur avant de commencer,
   et le tableau du plan lui fait bien varier les PV ennemis, la vitesse des
   projectiles, la cadence et les vies du joueur.

   La comparabilité des scores est réglée autrement, et mieux : seul le VÉTÉRAN
   produit un score classé (`RANKED_SCORE`). On ne compare donc jamais deux
   paliers, plutôt que d'aplatir les paliers pour pouvoir les comparer.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Difficulty = {
  Recrue: 0,
  Operateur: 1,
  Veteran: 2,
} as const;

export type DifficultyId = (typeof Difficulty)[keyof typeof Difficulty];

export const DIFFICULTY_COUNT = 3;

/** Nom affiché. Il vit ici et non dans l'interface : le nom fait partie de la
 *  définition du palier, et deux libellés divergents pour un même indice sont
 *  une confusion garantie le jour où l'on en ajoute un. */
export const DIFFICULTY_NAMES: readonly string[] = ['Recrue', 'Opérateur', 'Vétéran'];

/** Rayon de scellement de tir, en unités (PLAN.md §6.5).
 *
 *  Un ennemi plus proche que cette distance ne tire pas — son cooldown continue
 *  de tourner, pour qu'il ne lâche pas tout d'un coup quand le joueur s'écarte.
 *
 *  ⚠️ C'est le levier de difficulté le plus important du jeu, et le moins
 *  visible. Il ne change aucun chiffre affiché : il change la TAILLE DE LA
 *  PORTE DE SORTIE. À 140 u, un joueur acculé peut toujours foncer sur ce qui
 *  le menace ; à 70 u, cette échappatoire demande une précision qu'un débutant
 *  n'a pas. */
export const SEAL_RADIUS: readonly number[] = [140, 100, 70];

/** Le frôlement rapporte-t-il quelque chose ?
 *
 *  Faux en Recrue, et c'est délibéré (PLAN.md §6.8). Un système qui récompense
 *  de raser les projectiles apprend à un débutant exactement le geste qui le
 *  tue. Le frôlement reste visible à l'écran — l'anneau ne disparaît pas — mais
 *  il ne marque pas : on lui montre la mécanique sans l'inciter à la
 *  poursuivre avant d'en avoir les moyens. */
export const GRAZE_SCORES: readonly boolean[] = [false, true, true];

/** Les éventails statiques tournent-ils d'une volée à l'autre ?
 *
 *  C'est le changement de NATURE qu'exige le plan, et il tient en un booléen
 *  parce que la conséquence est structurelle et non quantitative : un éventail
 *  fixe laisse des couloirs au même endroit à chaque volée, donc mémorisables,
 *  donc un joueur peut s'y poster et cesser de lire. Le même éventail qui
 *  tourne de quelques degrés oblige à relire la figure à chaque fois.
 *
 *  Le nombre de projectiles, lui, ne change pas : c'est ce qui fait que la
 *  densité reste dans l'enveloppe mesurée et que le budget de frame tient. */
export const FAN_ROTATES: readonly boolean[] = [false, false, true];

/** Rotation appliquée aux éventails statiques en Vétéran, en degrés par volée.
 *
 *  ⚠️ 7° et pas davantage. Au-delà, deux volées successives ne se recouvrent
 *  plus du tout et la figure cesse d'être une figure : on obtient un balayage
 *  aléatoire, c'est-à-dire de la difficulté non apprenable — le défaut que le
 *  plan désigne comme le raccourci classique de l'amateur. À 7°, le couloir se
 *  déplace d'environ une largeur de vaisseau par volée : assez pour interdire
 *  de camper, assez peu pour rester suivi à l'œil. */
export const VETERAN_FAN_STEP_DEG = 7;

/* ── Les curseurs quantitatifs du tableau §6.10 ──────────────────────────────
   Ils ne remplacent pas les deux leviers structurels ci-dessus, ils les
   accompagnent. Le plan est explicite : « les difficultés changent la NATURE
   des patterns, PAS SEULEMENT les nombres » — pas seulement, donc aussi. */

/** Points de vie des ennemis : 75 % / 100 % / 135 %.
 *
 *  ⚠️ Se multiplie AVEC `LevelDef.hpScale` (coefficient de monde) et
 *  `balance.enemyHp`. Trois facteurs sur la même grandeur, ce qui est beaucoup —
 *  mais chacun répond à une question distincte : quel monde, quel palier, quel
 *  réglage global. Les fondre reviendrait à ne plus pouvoir en régler un sans
 *  déplacer les autres. */
export const ENEMY_HP_MUL: readonly number[] = [0.75, 1, 1.35];

/** Vitesse des projectiles ennemis : 82 % / 100 % / 112 %.
 *
 *  L'écart est volontairement plus resserré que celui des PV. Une balle plus
 *  rapide ne demande pas plus d'habileté au-delà d'un certain point : elle
 *  descend simplement sous le temps de réaction visuel (~250 ms), et la mort
 *  cesse d'être attribuable. +12 % laisse le plafond de 1 500 u/s intact. */
export const BULLET_SPEED_MUL: readonly number[] = [0.82, 1, 1.12];

/** Cadence des ennemis : 70 % / 100 % / 120 %. */
export const FIRE_RATE_MUL: readonly number[] = [0.7, 1, 1.2];

/** Vies accordées en plus (ou en moins) au démarrage : +2 / 0 / −1. */
export const LIVES_DELTA: readonly number[] = [2, 0, -1];

/** Le score obtenu compte-t-il pour un classement ?
 *
 *  Vrai au seul palier VÉTÉRAN. C'est la réponse du plan au problème de la
 *  comparabilité : plutôt que d'aplatir les paliers pour pouvoir les comparer,
 *  on ne compare que celui qui est comparable. Les deux autres restent des
 *  modes de jeu à part entière, avec leurs propres records locaux. */
export const RANKED_SCORE: readonly boolean[] = [false, false, true];

/** Ramène n'importe quelle entrée à un palier valide.
 *
 *  Une sauvegarde d'une version antérieure, un paramètre d'URL bricolé ou un
 *  champ absent doivent donner « Opérateur » — la référence — et jamais un
 *  index hors bornes qui produirait `undefined` dans les tables ci-dessus, donc
 *  un rayon de scellement `NaN`, donc des ennemis qui ne tirent plus jamais.
 *  La panne serait silencieuse et le jeu resterait jouable, simplement vide. */
export function clampDifficulty(d: number): DifficultyId {
  if (!(d >= 0)) return Difficulty.Operateur; // couvre aussi NaN
  const i = Math.floor(d);
  if (i > 2) return Difficulty.Veteran;
  return i as DifficultyId;
}
